"use client";

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef, useEffect } from 'react'
import { useForm } from '@tanstack/react-form'
import { Button } from '@/src/components/ui/button'
import { Input } from '@/src/components/ui/input'
import { Label } from '@/src/components/ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { Card } from '@/src/components/ui/card'
import { Badge } from '@/src/components/ui/badge'
import { Spinner } from '@/src/components/ui/spinner'
import { useTranslation } from '@/src/i18n/client'
import { listDrawings, createManualDrawing, saveDrawingBaseData } from '@/src/api/drawing'
import { createOperation, getOperationFormSchema } from '@/src/api/operation'
import { DynamicForm } from '@/src/components/manage/forms/DynamicForm'
import { ShortName } from '@/src/components/common/ShortName'
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  ArrowLeftIcon,
  GearIcon,
  LightningIcon,
  CaretRightIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'
import { resolveDrawingName, resolveDrawingNumber, detectSchemaType, getDrawingBaseData } from '@/src/lib/drawingHelpers'
import type { BaseDataField } from '@/src/lib/drawingHelpers'
import { defaultOperationFields, type FormSchemaDefinition } from '@/shared/schemas/operation-data.zod'

export const Route = createFileRoute('/_manage/manage/drawings/rapid')({
  component: LegacyManageRapidRedirect,
})

function LegacyManageRapidRedirect() {
  useEffect(() => {
    window.location.replace('/create')
  }, [])

  return null
}

type Step = 'select-drawing' | 'add-operation' | 'done'

const defaultFormSchema: FormSchemaDefinition = {
  id: 'default-operation',
  name: 'Operation',
  description: 'Record operation data',
  fields: defaultOperationFields,
  groups: [],
  layout: 'multi-col',
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** Build a save-ready DrawingData partial from raw text input values. */
function buildDrawingDataFromBaseValues(
  schemaName: string,
  values: Record<string, string>,
  fields: BaseDataField[],
): Record<string, any> {
  const obj: Record<string, any> = {}
  for (const field of fields) {
    const raw = values[field.key]
    if (raw === undefined || raw === '') continue
    obj[field.key] = field.inputType === 'number' ? parseFloat(raw) : raw
  }
  if (schemaName === 'gear') return { gear: [obj] }
  if (schemaName === 'tool') return { tool: [obj] }
  return {}
}

export function RapidEntryPage() {
  const { t } = useTranslation('operation')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>('select-drawing')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const [selectedDrawing, setSelectedDrawing] = useState<{
    id: string
    name?: string | null
    number?: string | null
    shareId?: string | null
    master?: { data: Record<string, any> } | null
  } | null>(null)
  const [selectedSchemaType, setSelectedSchemaType] = useState<'gear' | 'tool'>('gear')

  const [addedCount, setAddedCount] = useState(0)
  const [showQuickCreate, setShowQuickCreate] = useState(false)

  // Base data form state
  const [baseDataValues, setBaseDataValues] = useState<Record<string, string>>({})
  const [showBaseData, setShowBaseData] = useState(true)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Initialise base data values whenever the selected drawing changes
  useEffect(() => {
    if (!selectedDrawing) { setBaseDataValues({}); return }
    const raw = selectedDrawing.master?.data ?? null
    const type = detectSchemaType(raw)
    const schemaName = type === 'unknown' ? selectedSchemaType : type
    const fields = getDrawingBaseData(raw, schemaName)
    const initial: Record<string, string> = {}
    for (const f of fields) {
      if (f.value != null) initial[f.key] = String(f.value)
    }
    setBaseDataValues(initial)
  }, [selectedDrawing, selectedSchemaType])

  // Auto-focus search on mount
  useEffect(() => {
    if (step === 'select-drawing') {
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [step])

  // Drawing list query
  const { data: drawingsData, isLoading: isSearching } = useQuery({
    queryKey: ['drawings', 'rapid-search', debouncedSearch],
    queryFn: () =>
      listDrawings({
        data: {
          search: debouncedSearch || undefined,
          limit: 10,
          offset: 0,
          orderBy: 'createdAt',
          order: 'desc',
        },
      }),
    enabled: debouncedSearch.length >= 1 || step === 'select-drawing',
  })

  const drawings = drawingsData?.drawings ?? []

  // Form schema for operations
  const { data: formSchema } = useQuery({
    queryKey: ['operationFormSchema'],
    queryFn: async () => {
      try {
        return (await getOperationFormSchema({ data: {} })) || defaultFormSchema
      } catch {
        return defaultFormSchema
      }
    },
    staleTime: 1000 * 60 * 10,
  })

  // Operation name + basic fields
  const [operationName, setOperationName] = useState('')
  const [operationProvider, setOperationProvider] = useState('')
  const [operationSource, setOperationSource] = useState('manual')
  const operationNameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (step === 'add-operation') {
      setTimeout(() => operationNameRef.current?.focus(), 50)
    }
  }, [step])

  // Save base data mutation (called internally before creating an operation)
  const saveBaseDataMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDrawing) return
      const raw = selectedDrawing.master?.data ?? null
      const type = detectSchemaType(raw)
      const schemaName = type === 'unknown' ? selectedSchemaType : type
      const fields = getDrawingBaseData(raw, schemaName)
      const data = buildDrawingDataFromBaseValues(schemaName, baseDataValues, fields)
      // Skip save if no meaningful values were entered
      const firstArray = Object.values(data)[0]
      if (!firstArray || (Array.isArray(firstArray) && Object.keys(firstArray[0] ?? {}).length === 0)) return
      return saveDrawingBaseData({ data: { drawingId: selectedDrawing.id, data } })
    },
  })

  // Create operation mutation
  const createOperationMutation = useMutation({
    mutationFn: async (formData: Record<string, any>) => {
      if (!selectedDrawing) throw new Error('No drawing selected')
      // Persist base data alongside the operation
      await saveBaseDataMutation.mutateAsync()
      return createOperation({
        data: {
          drawingId: selectedDrawing.id,
          name: operationName,
          provider: operationProvider || undefined,
          source: operationSource,
          data: formData,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] })
      queryClient.invalidateQueries({ queryKey: ['drawings'] })
      toast.success(t('rapidEntry.operationSaved'), {
        description: t('rapidEntry.operationSavedDescription'),
      })
      setAddedCount((c) => c + 1)
      setOperationName('')
      setOperationProvider('')
      setOperationSource('manual')
    },
  })

  // Quick create drawing form
  const quickCreateForm = useForm({
    defaultValues: {
      name: '',
      number: '',
      company: '',
      drawingType: 'gear' as 'gear' | 'tool',
    },
    onSubmit: async ({ value }) => {
      await quickCreateMutation.mutateAsync(value)
    },
  })

  const quickCreateMutation = useMutation({
    mutationFn: async (values: { name: string; number: string; company: string; drawingType: 'gear' | 'tool' }) => {
      const toolData = values.drawingType === 'tool' ? [{ name: values.name }] : undefined
      return createManualDrawing({
        data: {
          data: {
            name: values.name,
            number: values.number || undefined,
            company: values.company || undefined,
            unitSystem: 'metric',
            language: 'en',
            general: [],
            ...(toolData ? { tool: toolData } : {}),
          },
        },
      })
    },
    onSuccess: (result: any, variables) => {
      toast.success(t('rapidEntry.drawingCreated'))
      setSelectedSchemaType(variables.drawingType)
      setSelectedDrawing({
        id: result.id,
        name: result.name,
        number: result.number,
        shareId: result.shareId,
        master: null,
      })
      setShowQuickCreate(false)
      setStep('add-operation')
      queryClient.invalidateQueries({ queryKey: ['drawings'] })
    },
  })

  function handleSelectDrawing(drawing: any) {
    const detected = detectSchemaType(drawing.master?.data ?? null)
    setSelectedSchemaType(detected === 'tool' ? 'tool' : 'gear')
    setSelectedDrawing({
      id: drawing.id,
      name: resolveDrawingName(drawing),
      number: resolveDrawingNumber(drawing),
      shareId: drawing.shareId,
      master: drawing.master ?? null,
    })
    setSearch('')
    setStep('add-operation')
  }

  function handleOperationSubmit(formData: Record<string, any>) {
    if (!operationName.trim()) return
    createOperationMutation.mutate(formData)
  }

  function handleChangeDrawing() {
    setStep('select-drawing')
    setAddedCount(0)
    setOperationName('')
    setOperationProvider('')
    setOperationSource('manual')
  }

  const drawingLabel = selectedDrawing
    ? selectedDrawing.name || selectedDrawing.number || selectedDrawing.shareId || selectedDrawing.id
    : ''

  // Derive schema type and base data fields for the selected drawing
  const rawMasterData = selectedDrawing?.master?.data ?? null
  const detectedType = detectSchemaType(rawMasterData)
  const schemaType = detectedType === 'unknown' ? selectedSchemaType : detectedType
  const schemaLabel = schemaType === 'gear' ? 'Gear' : schemaType === 'tool' ? 'Tool' : 'Drawing'
  const baseDataFields = getDrawingBaseData(rawMasterData, schemaType)

  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="mt-0 max-w-2xl mx-auto px-0 pb-0 sm:mt-6 sm:px-4 sm:pb-8">
        {step === 'add-operation' && selectedDrawing && (
          <div className="mb-4 flex items-center justify-end gap-2">
            {addedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {addedCount} saved
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleChangeDrawing}>
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              {t('rapidEntry.changeDrawing')}
            </Button>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <span
            className={`flex items-center gap-1 ${step === 'select-drawing' ? 'font-semibold text-primary' : 'text-muted-foreground'}`}
          >
            <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs font-bold">
              1
            </span>
            {t('rapidEntry.selectDrawing')}
          </span>
          <CaretRightIcon className="w-4 h-4 text-muted-foreground" />
          <span
            className={`flex items-center gap-1 ${step === 'add-operation' ? 'font-semibold text-primary' : 'text-muted-foreground'}`}
          >
            <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs font-bold">
              2
            </span>
            {t('rapidEntry.addOperation')}
          </span>
        </div>

        {/* STEP 1: Select Drawing */}
        {step === 'select-drawing' && (
          <div className="space-y-4">
            <Card className="p-4">
              <Label className="text-base font-semibold mb-3 block">{t('rapidEntry.selectDrawing')}</Label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  ref={searchRef}
                  placeholder={t('rapidEntry.searchDrawing')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  autoComplete="off"
                />
              </div>

              <div className="mt-3 space-y-1 max-h-72 overflow-y-auto">
                {isSearching && (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                )}
                {!isSearching && drawings.length === 0 && debouncedSearch && (
                  <p className="text-center text-muted-foreground py-4 text-sm">
                    {t('rapidEntry.noDrawingsFound')}
                  </p>
                )}
                {!isSearching && drawings.length === 0 && !debouncedSearch && (
                  <p className="text-center text-muted-foreground py-4 text-sm">
                    {t('rapidEntry.searchDrawing')}
                  </p>
                )}
                {drawings.map((drawing: any) => {
                  const name = resolveDrawingName(drawing)
                  const number = resolveDrawingNumber(drawing)
                  return (
                    <button
                      key={drawing.id}
                      onClick={() => handleSelectDrawing(drawing)}
                      className="w-full text-left px-3 py-2.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors flex items-center gap-3 group"
                    >
                      <GearIcon className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{name || <span className="text-muted-foreground italic">Untitled</span>}</div>
                        {number && <div className="text-xs text-muted-foreground">{number}</div>}
                      </div>
                      <CaretRightIcon className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100" />
                    </button>
                  )
                })}
              </div>
            </Card>

            {/* Quick Create Drawing */}
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setShowQuickCreate((v) => !v)}
              >
                <PlusCircleIcon className="w-4 h-4 mr-2" />
                {t('rapidEntry.quickAddDrawing')}
              </Button>

              {showQuickCreate && (
                <Card className="p-4 mt-2">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      quickCreateForm.handleSubmit()
                    }}
                    className="space-y-3"
                  >
                    <quickCreateForm.Field name="name">
                      {(field) => (
                        <div>
                          <Label htmlFor="qc-name">Drawing Name *</Label>
                          <Input
                            id="qc-name"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder="e.g. Spur Gear Z=32"
                            required
                          />
                        </div>
                      )}
                    </quickCreateForm.Field>
                    <quickCreateForm.Field name="number">
                      {(field) => (
                        <div>
                          <Label htmlFor="qc-number">Drawing Number</Label>
                          <Input
                            id="qc-number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder="e.g. DWG-2024-001"
                          />
                        </div>
                      )}
                    </quickCreateForm.Field>
                    <quickCreateForm.Field name="company">
                      {(field) => (
                        <div>
                          <Label htmlFor="qc-company">Company</Label>
                          <Input
                            id="qc-company"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder="e.g. Acme GmbH"
                          />
                        </div>
                      )}
                    </quickCreateForm.Field>
                    <quickCreateForm.Field name="drawingType">
                      {(field) => (
                        <div>
                          <Label htmlFor="qc-drawing-type">{t('rapidEntry.drawingType')}</Label>
                          <Select value={field.state.value} onValueChange={(v) => v && field.handleChange(v as 'gear' | 'tool')}>
                            <SelectTrigger id="qc-drawing-type" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gear">{t('rapidEntry.drawingTypeGear')}</SelectItem>
                              <SelectItem value="tool">{t('rapidEntry.drawingTypeTool')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </quickCreateForm.Field>
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQuickCreate(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={quickCreateMutation.isPending}
                      >
                        {quickCreateMutation.isPending ? (
                          <Spinner size={16} className="mr-2" />
                        ) : (
                          <PlusCircleIcon className="w-4 h-4 mr-2" />
                        )}
                        Create &amp; Select
                      </Button>
                    </div>
                  </form>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* STEP 2: Add Operation */}
        {step === 'add-operation' && selectedDrawing && (
          <div className="space-y-4">
            {/* Selected Drawing display */}
            <Card className="p-3 flex items-center gap-3 bg-muted/50">
              <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground">{t('rapidEntry.selectedDrawing')}</div>
                <div className="font-semibold truncate">{drawingLabel}</div>
              </div>
              {addedCount > 0 && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  {addedCount} op{addedCount !== 1 ? 's' : ''} added
                </Badge>
              )}
            </Card>

            {/* ── Base Data Card ── */}
            {detectedType === 'unknown' && (
              <Card className="p-4">
                <Label htmlFor="base-data-type">{t('rapidEntry.drawingType')}</Label>
                <Select value={selectedSchemaType} onValueChange={(v) => v && setSelectedSchemaType(v as 'gear' | 'tool')}>
                  <SelectTrigger id="base-data-type" className="w-full mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gear">{t('rapidEntry.drawingTypeGear')}</SelectItem>
                    <SelectItem value="tool">{t('rapidEntry.drawingTypeTool')}</SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            )}

            <Card className="overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                onClick={() => setShowBaseData((v) => !v)}
              >
                <span className="font-semibold text-sm flex items-center gap-2">
                  <GearIcon className="w-4 h-4" />
                  {schemaLabel} Base Data
                </span>
                {showBaseData
                  ? <CaretUpIcon className="w-4 h-4 text-muted-foreground" />
                  : <CaretDownIcon className="w-4 h-4 text-muted-foreground" />
                }
              </button>

              {showBaseData && (
                <div className="border-t">
                  {baseDataFields.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-4 py-3">
                      No base data fields available for this drawing type.
                    </p>
                  ) : (
                    <div className="divide-y divide-border/50">
                      {baseDataFields.map((field) => {
                        const hasValue = field.value != null
                        return (
                          <div key={field.key} className="flex items-center gap-3 px-4 py-2 text-sm">
                            {/* Label */}
                            <span className="text-muted-foreground shrink-0 w-36 text-xs">
                              {field.label}
                              {!field.optional && !hasValue && (
                                <span className="text-destructive ml-0.5">*</span>
                              )}
                            </span>
                            {/* Symbol */}
                            <span className="italic text-xs shrink-0 w-10 text-center text-muted-foreground">
                              <ShortName>{field.symbol}</ShortName>
                            </span>
                            {/* Value or Input */}
                            {hasValue ? (
                              <span className="font-semibold flex-1">
                                {String(field.value)}
                              </span>
                            ) : (
                              <div className="flex-1">
                                <Input
                                  id={`bd-${field.key}`}
                                  type={field.inputType ?? 'text'}
                                  step={field.inputType === 'number' ? 'any' : undefined}
                                  value={baseDataValues[field.key] ?? ''}
                                  onChange={(e) =>
                                    setBaseDataValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                  }
                                  placeholder="—"
                                  className="h-7 text-sm"
                                />
                              </div>
                            )}
                            {/* Unit */}
                            <span className="text-muted-foreground text-xs shrink-0 w-7 text-right">
                              {field.unit ?? ''}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground px-4 py-2 border-t border-border/50">
                    Missing values are saved to the drawing when the operation is submitted.
                  </p>
                </div>
              )}
            </Card>

            {/* Operation basic info */}
            <Card className="p-4">
              <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                <GearIcon className="w-4 h-4" />
                {t('operationInfo')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="op-name">{t('operationName')} *</Label>
                  <Input
                    id="op-name"
                    ref={operationNameRef}
                    value={operationName}
                    onChange={(e) => setOperationName(e.target.value)}
                    placeholder={t('operationNamePlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && operationName.trim()) {
                        e.preventDefault()
                      }
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="op-provider">{t('provider')}</Label>
                  <Input
                    id="op-provider"
                    value={operationProvider}
                    onChange={(e) => setOperationProvider(e.target.value)}
                    placeholder={t('providerPlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="op-source">{t('source')}</Label>
                  <Select
                    value={operationSource}
                    onValueChange={(v) => v && setOperationSource(v)}
                  >
                    <SelectTrigger id="op-source" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">{t('manual')}</SelectItem>
                      <SelectItem value="machine">{t('machine')}</SelectItem>
                      <SelectItem value="inspection">{t('inspection')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Dynamic form fields */}
            {formSchema && (
              <DynamicForm
                schema={formSchema}
                defaultValues={{}}
                onSubmit={handleOperationSubmit}
                onCancel={handleChangeDrawing}
                submitLabel={t('addOperation')}
                isPending={createOperationMutation.isPending || !operationName.trim()}
              />
            )}

            {!formSchema && (
              <Card className="p-4 flex justify-end gap-2">
                <Button variant="outline" onClick={handleChangeDrawing}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={() => handleOperationSubmit({})}
                  disabled={!operationName.trim() || createOperationMutation.isPending}
                >
                  {createOperationMutation.isPending && <Spinner size={16} className="mr-2" />}
                  {t('addOperation')}
                </Button>
              </Card>
            )}

            {createOperationMutation.error && (
              <p className="text-red-500 text-sm">
                {createOperationMutation.error instanceof Error
                  ? createOperationMutation.error.message
                  : 'Failed to save operation'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
